import React, { useContext, useEffect, useMemo, useState } from 'react';
import { LoaderAnimation } from 'components';
import { LendingContext } from 'context';
import { useFixedPage } from 'hooks';
import { TealiumService } from 'services';
import { lendingOnboardingSteps } from 'utils';
import './full-page-loader.scss';

const DID_YOU_KNOW_TIPS = [
  'You can access a free credit report from registered credit bureaus to review your credit information and spot any errors or suspicious activity.',
  'Borrowing only what you need and can comfortably afford to repay helps you stay in control of your finances and avoid unnecessary debt.',
  'Knowing your repayment date before taking a loan can help you plan ahead. Paying on time can also help build and maintain a positive credit record.',
  'Creating a simple monthly budget can help you identify spending patterns and save more effectively.',
  'Tracking your income and expenses each month can help you make informed borrowing decisions.',
];

const TIP_INTERVAL_MS = 5000;
const CREDIT_ENQUIRY_DURATION_MS = 3000;

const DidYouKnowCarousel = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  const shuffledTips = useMemo(() => {
    const arr = [...DID_YOU_KNOW_TIPS];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveIndex((i) => (i + 1) % shuffledTips.length);
    }, TIP_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [shuffledTips.length]);

  return (
    <div className='did-you-know'>
      <div className='did-you-know__viewport'>
        <div
          className='did-you-know__track'
          style={{ transform: `translateX(-${activeIndex * 100}%)` }}
        >
          {shuffledTips.map((tip, i) => (
            <div key={i} className='did-you-know__slide'>
              <p>
                <strong>Did you know?</strong> {tip}
              </p>
            </div>
          ))}
        </div>
      </div>
      <div className='did-you-know__dots'>
        {shuffledTips.map((_, i) => (
          <span
            key={i}
            className={`did-you-know__dot${i === activeIndex ? ' did-you-know__dot--active' : ''}`}
          />
        ))}
      </div>
    </div>
  );
};

export const FullPageLoader = ({ showLoader }) => {
  const { application } = useContext(LendingContext);
  const [showingQuotes, setShowingQuotes] = useState(false);
  useFixedPage(showLoader);

  useEffect(() => {
    if (!showLoader) {
      setShowingQuotes(false);
      return;
    }
    const timer = setTimeout(() => setShowingQuotes(true), CREDIT_ENQUIRY_DURATION_MS);
    return () => clearTimeout(timer);
  }, [showLoader]);

  useEffect(() => {
    if (application?.lastStep === lendingOnboardingSteps.offers_details) {
      TealiumService.onOfferRedirect(application.selectedOffer.companyName);
    }
  }, []);

  function getLoaderContent() {
    if (application?.lastStep === lendingOnboardingSteps.offers_details) {
      return (
        <div>
          <h2>Redirecting you to {application.selectedOffer.companyName}</h2>
          <p>Complete the application on the provider website</p>
        </div>
      );
    }

    if (showingQuotes) {
      return (
        <div>
          <h2>Getting your quotes ready</h2>
          <p>This should only take a moment</p>
        </div>
      );
    }

    return (
      <div>
        <h2>We're completing a credit enquiry</h2>
        <p>This should only take a moment</p>
      </div>
    );
  }

  return (
    <div>
      {showLoader && (
        <div className='FullPageLoader'>
          <div className='form-details-container'>
            <div className='form-details-container__content loader-modal'>
              <div className='animation'>
                <LoaderAnimation height={160} width={120} />
              </div>
              {getLoaderContent()}
              <DidYouKnowCarousel />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
